import { Route, Routes, useLocation } from "react-router";
import { Toaster, cn } from "@bloom/ui";
import { TopNav } from "./components/top-nav.tsx";
import { Footer } from "./components/footer.tsx";
import { LandingPage } from "./routes/landing.tsx";
import { SeriesIndexPage } from "./routes/series-index.tsx";
import { SeriesDetailPage } from "./routes/series-detail.tsx";
import { WatchBySlugPage } from "./routes/watch.tsx";
import { LoginPage } from "./routes/login.tsx";
import { SignupPage } from "./routes/signup.tsx";
import { VerifyEmailPage } from "./routes/verify-email.tsx";
import { ForgotPasswordPage } from "./routes/forgot-password.tsx";
import { ResetPasswordPage } from "./routes/reset-password.tsx";
import { AccountPage } from "./routes/account.tsx";
import { SearchPage } from "./routes/search.tsx";
import { MyListPage } from "./routes/my-list.tsx";
import { UnlockPage } from "./routes/unlock.tsx";
import { NotFoundPage } from "./routes/not-found.tsx";
import { ServerErrorPage } from "./routes/server-error.tsx";
import { MaintenancePage } from "./routes/maintenance.tsx";
import { TermsPage } from "./routes/terms.tsx";
import { PrivacyPage } from "./routes/privacy.tsx";
import { CookiesPage } from "./routes/cookies.tsx";
import { RefundPolicyPage } from "./routes/refund-policy.tsx";
import { AboutPage } from "./routes/about.tsx";
import { FaqPage } from "./routes/faq.tsx";
import { ContactUsPage } from "./routes/contact-us.tsx";
import { ReportAProblemPage } from "./routes/report-a-problem.tsx";
import { CookieConsent } from "./components/cookie-consent.tsx";

// Operator-toggled flag — set VITE_MAINTENANCE_MODE=true in the deploy env to
// short-circuit every customer route to the maintenance page without having
// to redeploy code. The flag is read at build time by Vite, so this is a
// hard switch rather than a runtime override.
const MAINTENANCE_MODE = import.meta.env.VITE_MAINTENANCE_MODE === "true";

export function App() {
  const location = useLocation();
  // On the landing route the hero already extends behind the (transparent)
  // fixed nav, so no top padding. Every other route gets pt-16 so the nav
  // height doesn't occlude content.
  const onHome = location.pathname === "/";

  // Maintenance mode bypasses the TopNav, Footer, and every route — we want
  // a fully isolated "we'll be right back" experience, not a chrome with
  // broken nav links pointing at endpoints that are intentionally down.
  if (MAINTENANCE_MODE) {
    return (
      <div className="min-h-screen text-[var(--brand-ink)]" data-theme="light">
        <MaintenancePage />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col text-[var(--brand-ink)]" data-theme="light">
      <TopNav />
      <main className={cn("relative flex-1 bg-[var(--brand-paper)]", onHome ? "" : "pt-16")}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/series" element={<SeriesIndexPage />} />
          <Route path="/series/:slug" element={<SeriesDetailPage />} />
          <Route path="/watch/:slug/:season/:episode" element={<WatchBySlugPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/account/*" element={<AccountPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/my-list" element={<MyListPage />} />
          <Route path="/unlock/:kind/:targetId" element={<UnlockPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/cookies" element={<CookiesPage />} />
          <Route path="/refund-policy" element={<RefundPolicyPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/contact-us" element={<ContactUsPage />} />
          <Route path="/report-a-problem" element={<ReportAProblemPage />} />
          <Route path="/server-error" element={<ServerErrorPage />} />
          <Route path="/maintenance" element={<MaintenancePage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <Footer />
      <CookieConsent />
      <Toaster />
    </div>
  );
}
